import { lazy, useEffect, useState } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { generatePath } from 'react-router-dom';

import { fetchPDF } from '#client/components/StatementsModal/utils/pdf';
import { MODAL_NAMES } from '#client/constants/modals';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { DEFAULT_SEGMENT } from '#client/manifest';
import { checkNotAllowedBrowsersForPDF } from '#client/utils/browserVersion';

import Document from '../Document/Document';
import EmptyStatement from '../MonthlyStatementsTab/components/EmptyStatement';

import '#client/components/ViewPdfModal/utils/polyfill';

const ViewPdfModal = lazy(() => import('#client/components/ViewPdfModal'));
const ExitConfirmModal = lazy(
  () => import('#client/components/CheckDeposit/components/ExitConfirmModal')
);

export default function BankAccountVerification() {
  const { unitAccountId, isEmptyUnitAccountId } = useUnitAccountId();

  const {
    unitAccount,
    refetch: refetchUnitAccount,
    loading: loadingUnitAccount
  } = useUnitAccount();
  const { accessToken } = useAuthentication();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const { openModal, closeModal } = useModalContext();
  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });
  const [loading, setLoading] = useState(false);

  const last4Digits = unitAccount?.accountNumber?.slice(-4) || '####';
  const period = dayjs().format('YYYY-MM');

  // Note: generatePath is a function that will crash if params are empty,
  // so we need to make sure they are not empty before calling it
  const createEndpoint = () =>
    generatePath(
      `${DEFAULT_SEGMENT}/statements/:businessEntityUuid/bank-verification?period=${period}`,
      { businessEntityUuid }
    );
  const documentTitle = `Bank Account Verification (..${last4Digits})`;

  const viewPDF = async (customerToken) => {
    // check if user is supported
    const isNotSupportedBrowser = checkNotAllowedBrowsersForPDF();
    if (isNotSupportedBrowser) {
      openModal({
        component: ExitConfirmModal,
        title: 'Browser update recommended.',
        description: 'For the best experience, please update your browser or try another one.',
        okText: 'Update',
        onOk: () => {
          window.open('https://www.dev.zenbusiness.com/d/unsupported-browsers');
        }
      });
      setLoading(false);
      return;
    }

    try {
      const { blob, fileName } = await fetchPDF({
        endpoint: createEndpoint(),
        customerToken,
        accessToken
      });
      // convert to pdf base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        openModal({
          name: MODAL_NAMES.VIEW_PDF_MODAL,
          component: ViewPdfModal,
          link: reader.result,
          title: documentTitle,
          onSave: () => saveAs(blob, fileName),
          handleBack: () => closeModal(MODAL_NAMES.VIEW_PDF_MODAL)
        });
      };
    } catch (error) {
      createCommonErrorToast({ error });
      setLoading(false);
    }
  };

  const downloadPDF = async (customerToken) => {
    try {
      const { blob, fileName } = await fetchPDF({
        endpoint: createEndpoint(),
        customerToken,
        accessToken
      });
      saveAs(blob, fileName);
    } catch (error) {
      createCommonErrorToast({ error });
      setLoading(false);
    }
  };

  const fetchAndHandlePDF = (callback) => async () => {
    setLoading(true);

    const customerToken = await getCustomerToken();
    if (!customerToken) {
      return;
    }

    await callback(customerToken);

    setLoading(false);
  };

  useEffect(() => {
    /**
     * When:
     * unitAccountId is no empty (if empty return empty state)
     * unitAccount is not available (if available return last 4 digits of account number)
     * loading unitAccount is false
     * => refetch unitAccount to get account number
     */
    if (!isEmptyUnitAccountId && !unitAccount && !loadingUnitAccount) {
      refetchUnitAccount();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmptyUnitAccountId, unitAccount]);

  return unitAccountId ? (
    <Document
      disabled={loading}
      handleDownload={fetchAndHandlePDF(downloadPDF)}
      handleView={fetchAndHandlePDF(viewPDF)}
      loading={loading}
      text={`Bank Account Verification (..${last4Digits})`}
    />
  ) : (
    <EmptyStatement />
  );
}
